import { type StatementTransaction } from "@kitchen/schemas";
import { Button, Chip, faDigits, formatDate, formatMoney } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, financeApi, type TreeNode } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

const TX_TYPE_LABELS: Record<
  StatementTransaction["type"],
  { label: string; tone: "ok" | "coral" | "warn" | "blue" }
> = {
  invoice: { label: "فاکتور مصرف", tone: "blue" },
  "credit-note": { label: "یادداشت بستانکار", tone: "ok" },
  "debit-note": { label: "یادداشت بدهکار", tone: "warn" },
  payment: { label: "دریافت وجه", tone: "ok" },
  "cheque-bounced": { label: "برگشت چک", tone: "coral" },
};

export function StatementTab() {
  const tree = useQuery({ queryKey: ["organizations"], queryFn: () => api<TreeNode[]>("/organizations") });

  const orgList: { id: string; name: string }[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      orgList.push({ id: n.id, name: n.name });
      walk(n.children);
    }
  };
  walk(tree.data ?? []);

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [asOfDate, setAsOfDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  // High debt threshold modal state
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [thresholdInput, setThresholdInput] = useState<number>(0);

  const activeOrgId = selectedOrgId || orgList[0]?.id || "";

  const statementQuery = useQuery({
    queryKey: ["finance", "statement", activeOrgId, asOfDate],
    queryFn: () => (activeOrgId ? financeApi.getStatement(activeOrgId, asOfDate) : null),
    enabled: !!activeOrgId,
  });

  const thresholdAction = useAction(
    async ({ orgId, threshold }: { orgId: string; threshold: number }) => {
      await financeApi.setHighDebtThreshold(orgId, threshold);
      setShowThresholdModal(false);
    },
    [["finance", "statement"]],
  );

  const stmt = statementQuery.data;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Top Controls */}
      <div className="panel">
        <div className="row wrap-between" style={{ alignItems: "center", gap: "var(--s3)" }}>
          <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
            <div>
              <label htmlFor="stmt-org" className="label t-cap muted">
                سازمان
              </label>
              <select
                id="stmt-org"
                className="input"
                value={activeOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                }}
              >
                {orgList.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="stmt-asof" className="label t-cap muted">
                تا تاریخ (مبنای صورتحساب و سن بدهی)
              </label>
              <input
                id="stmt-asof"
                type="date"
                className="input"
                value={asOfDate}
                onChange={(e) => {
                  setAsOfDate(e.target.value);
                }}
              />
            </div>
          </div>

          <Button
            variant="ghost"
            onClick={() => {
              setShowThresholdModal(true);
            }}
          >
            تنظیم سقف مجاز بدهی (هشدار)
          </Button>
        </div>
      </div>

      {statementQuery.isLoading && <p className="muted">در حال محاسبه صورتحساب و سن بدهی...</p>}
      {statementQuery.isError && <ErrorLine error="خطا در دریافت اطلاعات صورتحساب" />}

      {stmt && (
        <>
          {/* High Debt Badge Warning (P-01: never blocks orders) */}
          {stmt.balance > 50_000_000 && (
            <div
              className="card alert coral row-between"
              style={{
                alignItems: "center",
                backgroundColor: "var(--bg-card)",
                borderRight: "4px solid var(--coral-strong)",
              }}
            >
              <div>
                <span className="bold">هشدار بدهی بالا: </span>
                <span>
                  مانده بدهی سازمان ({formatMoney(stmt.balance, "rial")}) در سطح هشدار قرار دارد. سفارش‌ها
                  مسدود نمی‌شوند ولی پیگیری مالی الزامی است.
                </span>
              </div>
              <Chip tone="coral">وضعیت بدهی: بالا</Chip>
            </div>
          )}

          {/* KPI Summary Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "var(--s3)",
            }}
          >
            <div className="card">
              <span className="t-cap muted">کل فاکتورهای صادرشده</span>
              <div className="bold t-title" style={{ marginTop: "4px" }}>
                {formatMoney(stmt.totalInvoiced, "rial")}
              </div>
            </div>

            <div className="card">
              <span className="t-cap muted">کل پرداخت‌ها و وصولی‌ها</span>
              <div className="bold t-title" style={{ marginTop: "4px", color: "var(--blue-ink)" }}>
                {formatMoney(stmt.totalPaid, "rial")}
              </div>
            </div>

            <div className="card">
              <span className="t-cap muted">مانده نهایی حساب</span>
              <div
                className="bold t-title"
                style={{
                  marginTop: "4px",
                  color: stmt.balance > 0 ? "var(--coral-strong)" : "var(--ok-ink, green)",
                }}
              >
                {stmt.balance > 0
                  ? `${formatMoney(stmt.balance, "rial")} (بدهکار)`
                  : stmt.balance < 0
                    ? `${formatMoney(Math.abs(stmt.balance), "rial")} (بستانکار)`
                    : "تسویه کامل (۰ ریال)"}
              </div>
            </div>

            <div className="card">
              <span className="t-cap muted">اعتبار تخصیص‌نیافته</span>
              <div className="bold t-title" style={{ marginTop: "4px" }}>
                {formatMoney(stmt.unallocatedCredit, "rial")}
              </div>
            </div>
          </div>

          {/* Aging Buckets Card */}
          <div className="panel" title="تفکیک سنی مانده بدهی فاکتورها (Aging)">
            <p className="t-cap muted" style={{ marginBottom: "var(--s3)" }}>
              محاسبه دیرکرد سررسید فاکتورهای باز بر اساس پایان دوره هر فاکتور تا تاریخ مبنا (
              {formatDate(stmt.asOfDate)})
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "var(--s3)",
              }}
            >
              <div className="card" style={{ backgroundColor: "var(--bg-subtle)" }}>
                <span className="t-cap muted">جاری (۰ تا ۳۰ روز)</span>
                <div className="bold" style={{ marginTop: "4px" }}>
                  {formatMoney(stmt.aging.current0to30, "rial")}
                </div>
              </div>

              <div className="card" style={{ backgroundColor: "var(--bg-subtle)" }}>
                <span className="t-cap muted">۳۱ تا ۶۰ روز</span>
                <div className="bold" style={{ marginTop: "4px" }}>
                  {formatMoney(stmt.aging.days31to60, "rial")}
                </div>
              </div>

              <div className="card" style={{ backgroundColor: "var(--bg-subtle)" }}>
                <span className="t-cap muted">۶۱ تا ۹۰ روز</span>
                <div className="bold" style={{ marginTop: "4px" }}>
                  {formatMoney(stmt.aging.days61to90, "rial")}
                </div>
              </div>

              <div className="card" style={{ backgroundColor: "var(--bg-subtle)" }}>
                <span className="t-cap muted">بیش از ۹۰ روز</span>
                <div
                  className="bold"
                  style={{
                    marginTop: "4px",
                    color: stmt.aging.days90Plus > 0 ? "var(--coral-strong)" : undefined,
                  }}
                >
                  {formatMoney(stmt.aging.days90Plus, "rial")}
                </div>
              </div>

              <div className="card" style={{ backgroundColor: "var(--bg-subtle)" }}>
                <span className="t-cap bold">مجموع معوقات (بیش از ۳۰ روز)</span>
                <div
                  className="bold"
                  style={{
                    marginTop: "4px",
                    color: stmt.aging.totalOverdue > 0 ? "var(--coral-strong)" : undefined,
                  }}
                >
                  {formatMoney(stmt.aging.totalOverdue, "rial")}
                </div>
              </div>
            </div>
          </div>

          {/* Statement Timeline Table */}
          <div className="panel" title={`ریز تراکنش‌های صورتحساب ${stmt.organizationName}`}>
            {stmt.transactions.length === 0 ? (
              <p className="muted text-center" style={{ padding: "var(--s4) 0" }}>
                هیچ تراکنش مالی در این دوره ثبت نشده است.
              </p>
            ) : (
              <table className="table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>نوع سند</th>
                    <th>شماره مرجع</th>
                    <th>شرح سند مالی</th>
                    <th>بدهکار (ریال)</th>
                    <th>بستانکار (ریال)</th>
                    <th>مانده (ریال)</th>
                  </tr>
                </thead>
                <tbody>
                  {stmt.transactions.map((tx) => {
                    const badge = TX_TYPE_LABELS[tx.type];
                    return (
                      <tr key={`${tx.type}-${tx.referenceId}-${tx.date}`}>
                        <td>{formatDate(tx.date)}</td>
                        <td>
                          <Chip tone={badge.tone}>{badge.label}</Chip>
                        </td>
                        <td className="bold">{faDigits(tx.referenceNumber)}</td>
                        <td>{tx.description}</td>
                        <td className="bold">{tx.debit > 0 ? formatMoney(tx.debit, "rial") : "—"}</td>
                        <td className="bold" style={{ color: tx.credit > 0 ? "var(--blue-ink)" : undefined }}>
                          {tx.credit > 0 ? formatMoney(tx.credit, "rial") : "—"}
                        </td>
                        <td
                          className="bold"
                          style={{
                            color: tx.balance > 0 ? "var(--coral-strong)" : undefined,
                          }}
                        >
                          {formatMoney(tx.balance, "rial")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Modal: Set High Debt Threshold */}
      {showThresholdModal && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "450px", width: "90%" }}>
            <h2 className="t-title">تنظیم سقف بدهی مجاز سازمان</h2>
            <p className="muted t-cap">
              در صورت عبور بدهی سازمان از این سقف، نشان هشدار در پیشخوان مدیر فعال می‌شود (بدون توقف
              سفارش‌ها).
            </p>

            <div className="stack" style={{ gap: "var(--s3)", margin: "var(--s4) 0" }}>
              <div>
                <label htmlFor="thr-input" className="label t-cap">
                  مبلغ سقف هشدار بدهی (ریال)
                </label>
                <input
                  id="thr-input"
                  type="number"
                  className="input"
                  placeholder="مثال: ۱۰۰۰۰۰۰۰۰"
                  value={thresholdInput || ""}
                  onChange={(e) => {
                    setThresholdInput(Number(e.target.value));
                  }}
                />
                {thresholdInput > 0 && (
                  <p className="hint muted" style={{ marginTop: "4px" }}>
                    معادل: {formatMoney(thresholdInput, "toman")}
                  </p>
                )}
              </div>

              <ErrorLine error={thresholdAction.error} />
            </div>

            <div className="row wrap" style={{ justifyContent: "flex-end", gap: "var(--s3)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowThresholdModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={thresholdAction.busy || thresholdInput <= 0}
                onClick={() => {
                  thresholdAction.run({ orgId: activeOrgId, threshold: thresholdInput });
                }}
              >
                {thresholdAction.busy ? "در حال ثبت..." : "ذخیره سقف بدهی"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
